/**
 * /verify-email#token=… — 開発者のメールの確認（確認メールのリンク先）。2026-09-28 creator/email-unverified で AccountRecovery.tsx から分けた。
 *
 * サーバーの正: apps/server/src/modules/platform/recovery.ts（Core SEC-DEVLOGIN-08）。判断は ../emailVerify.ts（テストはそちら）。
 *   手元に kds_ がある（登録した同じブラウザ）→ 開いた瞬間に POST（Bearer 付き）→ { status: 'verified' } = 今までどおり
 *   kds_ が無い（別の端末・メールの事前スキャン）→ **自動で送らない**。「このメールアドレスを確認する」を押してから送る
 *     WHY: 会社のメールの安全装置が届いたリンクを開いて JavaScript まで動かすことがある。kds_ 無しで送ると password_required になり、
 *     サーバーはその時点でパスワードを無効にする = 本人が何もしないうちにログインできなくなる（emailVerify.ts shouldAutoVerify）
 *   { status: 'password_required', resetToken } → 新しいパスワードの欄（パスワードの再設定と同じ形）→ POST /v1/developers/password/reset
 *     → 外したログイン方法・残っているサーバーの鍵があれば /reset-password/done（pages/ResetDone.tsx）→ /login へ移って
 *       「新しいパスワードでログインしてください」（reset-password の成功と同じ = パスワード管理が保存を出す）
 *     resetToken はこのコンポーネントの state（メモリ）にだけ置く。URL・ストレージに残さない（再読み込みで消える = 「パスワードを忘れた」へ）。
 *     期限（30 分）切れ・invalid_token のときも「パスワードを忘れた」へ誘導する（サーバーはもうパスワードを無効にしているので、そこが唯一の戻り道）
 * トークン（確認のもの）は URL の fragment で受け、読んだらすぐ URL から消す（AccountRecovery.tsx の useFragmentToken）。
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, getToken, type ApiError } from '../api';
import { authFormProps, MIN_PASSWORD, passwordFieldProps, passwordLengthOk } from '../authForms';
import { resetExpired, shouldAutoVerify, verifyOutcome, type VerifyResponse } from '../emailVerify';
import { errorText, useT } from '../i18n';
import { inviteCreditedByVerify, readInviteStanding, verifyInviteDecision } from '../inviteCode';
import { money } from '../money';
import { useSession } from '../session';
import { AuthShell, navigateAfterReset, ResetAccountField, useFragmentToken, useResetAccount } from './AccountRecovery';

type State =
  | { kind: 'ask' } // kds_ が無い: ボタンを押すまで送らない
  | { kind: 'working' }
  // invite = このクリックで入った招待クレジット（C5・分かるときだけ）
  | { kind: 'done'; invite?: { amount: number; currency: string } }
  | { kind: 'error'; message: string }
  | { kind: 'password'; resetToken: string; expiresAt: number | null };

export function VerifyEmailPage() {
  const { t } = useT();
  const token = useFragmentToken();
  const { developer, updateDeveloper } = useSession();
  const navigate = useNavigate();
  // 開いた時点の判断を 1 回だけ（途中でログイン状態が変わっても自動で送り直さない）
  const [auto] = useState(() => shouldAutoVerify(getToken()));
  const [state, setState] = useState<State>(() => (!token ? { kind: 'error', message: t('recover.missingToken') } : auto ? { kind: 'working' } : { kind: 'ask' }));
  // 開いた時点でメールが未確認だったか（C5: 確認済みの人の 2 回目のクリックで「招待クレジットが入りました」と言わない・inviteCode.ts）
  const [wasUnverified] = useState(() => developer?.emailVerified === false);
  // StrictMode の二重実行で 2 回 POST すると 2 回目が invalid_token になる。1 回だけ送る
  const sent = useRef(false);

  const send = async () => {
    if (!token || sent.current) return;
    sent.current = true;
    setState({ kind: 'working' });
    try {
      // kds_ があれば api() が Bearer を付ける（本人のセッション = 確認）。無ければ付かない（= password_required の流れ）
      const res = await api<VerifyResponse>('/v1/developers/email/verify', { method: 'POST', body: { token }, auth: auto });
      const out = verifyOutcome(res);
      if (out.kind === 'verified') {
        // 招待クレジットが入ったか（2026-09-30 console/core-first C5 → console/invite-fields で応答の invite を使う形に）。
        // 新しい本体は応答の invite で決まる（inviteCode.ts verifyInviteDecision）。キーが無い古い本体だけ、今までどおり残高の invite を読み直す。
        // 読めない・分からないときは「確認しました」だけ（確認そのものは済んでいる = 失敗は表に出さない）
        let invite: { amount: number; currency: string } | undefined;
        const decision = verifyInviteDecision(res);
        if (decision.kind === 'credited') invite = { amount: decision.amount, currency: decision.currency };
        else if (decision.kind === 'legacy' && wasUnverified) {
          try {
            const p = await api<{ currency?: string; invite?: unknown }>('/v1/money/prepaid');
            const amount = inviteCreditedByVerify(true, readInviteStanding(p.invite));
            if (amount !== null && typeof p.currency === 'string') invite = { amount, currency: p.currency };
          } catch {
            // 残高が読めない = 分からない
          }
        }
        setState({ kind: 'done', invite });
        updateDeveloper({ emailVerified: true });
      } else {
        setState({ kind: 'password', resetToken: out.resetToken, expiresAt: out.expiresAt });
      }
    } catch (err) {
      setState({ kind: 'error', message: errorText(err as ApiError) });
    }
  };

  useEffect(() => {
    if (auto) void send();
  }, []);

  if (state.kind === 'password') return <ChoosePassword resetToken={state.resetToken} expiresAt={state.expiresAt} />;

  return (
    // 見出しは状態で分ける（2026-11-05 core/first5）: 済んだ = 「Email confirmed」・それ以外（押す前・確かめている間・失敗）= 「Confirm your email address」
    <AuthShell title={t(state.kind === 'done' ? 'verify.doneTitle' : 'recover.verifyTitle')} lead={state.kind === 'ask' ? t('verify.confirmLead') : undefined}>
      {state.kind === 'ask' && (
        <>
          <button type="button" className="btn btn-primary btn-block" onClick={() => void send()}>
            {t('verify.confirmButton')}
          </button>
          <p className="small muted">{t('verify.otherDeviceNote')}</p>
        </>
      )}
      {state.kind === 'working' && <p className="muted">{t('recover.verifying')}</p>}
      {state.kind === 'done' && (
        <p className="alert alert-ok" role="status">
          {t('recover.verified')}
        </p>
      )}
      {state.kind === 'done' && state.invite && (
        <p className="alert alert-ok" role="status">
          {t('recover.verifiedInvite', { amount: money(state.invite.amount, state.invite.currency) })}
        </p>
      )}
      {state.kind === 'error' && (
        <p className="alert alert-error" role="alert">
          {state.message}
        </p>
      )}
      {state.kind !== 'ask' && state.kind !== 'working' && (
        <button type="button" className="btn btn-primary btn-block" onClick={() => navigate(developer ? '/' : '/login')}>
          {developer ? t('recover.continue') : t('auth.logIn')}
        </button>
      )}
    </AuthShell>
  );
}

/** 「パスワードを忘れた」へのリンク（途中でやめた・期限切れ・invalid_token の戻り道） */
function ForgotLink() {
  const { t } = useT();
  return <Link to="/forgot-password">{t('verify.forgotLink')}</Link>;
}

/**
 * password_required のあとの新しいパスワード。形は ResetPasswordPage（AccountRecovery.tsx）と同じ（new-password と確認の欄・送信 → 遷移）。
 * フォームの id も同じ 'reset'（パスワード管理にとっては「メールのリンクから新しいパスワードを決める」同じ画面）
 */
function ChoosePassword({ resetToken, expiresAt }: { resetToken: string; expiresAt: number | null }) {
  const { t, tr } = useT();
  const { developer, signOut } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 期限切れ・トークンが使えない = この画面ではもう決められない。「パスワードを忘れた」だけを出す
  const [dead, setDead] = useState(() => !resetToken || resetExpired(expiresAt, Date.now()));
  // アカウント名（2026-10-09 core/reset-username）: /reset-password と同じ。パスワード管理が新しいパスワードをこのアカウントに保存できるように
  const account = useResetAccount(dead ? null : resetToken);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (resetExpired(expiresAt, Date.now())) return setDead(true);
    if (!passwordLengthOk(password)) return setError(t('auth.passwordTooShort', { min: MIN_PASSWORD }));
    if (password !== confirm) return setError(t('recover.mismatch'));
    setBusy(true);
    try {
      const res = await api<unknown>('/v1/developers/password/reset', { method: 'POST', body: { token: resetToken, password }, auth: false });
      // サーバーは全セッションを失効済み。この端末のログイン状態も捨てる（別のアカウントのセッションでも、確認の後はログインし直してもらう）
      if (developer) await signOut();
      // 外したログイン方法・残っているサーバーの鍵があれば完了の画面へ（再設定と同じ・2026-09-28 creator/dashboard-pass）
      navigateAfterReset(navigate, res, account);
    } catch (err) {
      const e2 = err as ApiError;
      if (e2.code === 'invalid_token') setDead(true);
      else setError(errorText(e2));
      setBusy(false);
    }
  };

  if (dead) {
    return (
      <AuthShell title={t('verify.passwordTitle')}>
        <p className="alert alert-error" role="alert">
          {tr('verify.expired', { link: <ForgotLink /> })}
        </p>
      </AuthShell>
    );
  }
  return (
    <AuthShell title={t('verify.passwordTitle')} lead={t('verify.passwordLead')}>
      {account && <p className="muted">{t('recover.resetFor', { email: account })}</p>}
      <form className="auth-form" {...authFormProps('reset', '/verify-email')} onSubmit={(e) => void submit(e)}>
        <ResetAccountField email={account} />
        <label className="field" htmlFor="new-password">
          <span>{t('recover.newPassword')}</span>
          <input {...passwordFieldProps('new')} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus aria-describedby="pw-hint" />
          <small id="pw-hint" className="muted">
            {t('auth.passwordHint', { min: MIN_PASSWORD })}
          </small>
        </label>
        <label className="field" htmlFor="confirm-password">
          <span>{t('recover.confirmPassword')}</span>
          <input {...passwordFieldProps('new', { confirm: true })} value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </label>
        {error && (
          <p className="alert alert-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? t('auth.busy') : t('recover.save')}
        </button>
      </form>
      <p className="small muted">{tr('verify.stopNote', { link: <ForgotLink /> })}</p>
    </AuthShell>
  );
}
